import { ApiError } from '@/api/errors'
import type {
  BillingPackage,
  CashSaleInput,
  CashSaleResult,
  CheckoutChannel,
  CheckoutIntent,
  CheckoutIntentInput,
  EndPlanResult,
  Money,
  Purchase,
  PurchaseLot,
  RefundInput,
  RefundResult,
} from '@/features/actions/types'
import { formatPrice } from '@/lib/utils/format'
import { toCents } from '@/lib/utils/money'
import { useAuthStore } from '@/stores/authStore'
import type { Credits } from '@/types/models'

/**
 * The Ablefy catalogue and a purchase history per customer, in memory, in the
 * shapes the billing endpoints send. A cash sale only grants credits, as on
 * the server, so it never appears in the history.
 */

type PackageKind = 'trial' | 'package' | 'subscription'

interface DemoRow {
  id: string
  slug: string
  name: string
  kind: PackageKind
  creditQty: number
  termMonths: number | null
  pricingPlanId: number
  rateCents: number
  rateCount: number
}

/**
 * Placeholder names and prices for the 16.10 show, as DiEM's PM set them on 15.10:
 * 10er 1.400 €, 20er 2.600 €, instalments in three rates, Abos at four treatments a
 * month. AiL has not set the real ones yet.
 */
const CATALOGUE: DemoRow[] = [
  {
    id: '1',
    slug: 'abo-6-monate',
    name: 'Abo 6 Monate',
    kind: 'subscription',
    creditQty: 4,
    termMonths: 6,
    pricingPlanId: 7301,
    rateCents: 45900,
    rateCount: 6,
  },
  {
    id: '2',
    slug: '10er-paket',
    name: '10er-Paket',
    kind: 'package',
    creditQty: 10,
    termMonths: null,
    pricingPlanId: 7302,
    rateCents: 140000,
    rateCount: 1,
  },
  {
    id: '3',
    slug: '10er-paket-raten',
    name: '10er-Paket · 3 Raten',
    kind: 'package',
    creditQty: 10,
    termMonths: null,
    pricingPlanId: 7303,
    rateCents: 47000,
    rateCount: 3,
  },
  {
    id: '4',
    slug: '20er-paket',
    name: '20er-Paket',
    kind: 'package',
    creditQty: 20,
    termMonths: null,
    pricingPlanId: 7304,
    rateCents: 260000,
    rateCount: 1,
  },
  {
    id: '5',
    slug: '20er-paket-raten',
    name: '20er-Paket · 3 Raten',
    kind: 'package',
    creditQty: 20,
    termMonths: null,
    pricingPlanId: 7305,
    rateCents: 87000,
    rateCount: 3,
  },
  {
    id: '6',
    slug: 'probebehandlung',
    name: 'Probebehandlung',
    kind: 'trial',
    creditQty: 1,
    termMonths: null,
    pricingPlanId: 7306,
    rateCents: 4900,
    rateCount: 1,
  },
  {
    id: '7',
    slug: 'abo-3-monate',
    name: 'Abo 3 Monate',
    kind: 'subscription',
    creditQty: 4,
    termMonths: 3,
    pricingPlanId: 7307,
    rateCents: 49900,
    rateCount: 3,
  },
  {
    id: '8',
    slug: 'abo-12-monate',
    name: 'Abo 12 Monate',
    kind: 'subscription',
    creditQty: 4,
    termMonths: 12,
    pricingPlanId: 7308,
    rateCents: 41900,
    rateCount: 12,
  },
]

const KIND_LABELS: Record<PackageKind, string> = {
  trial: 'Probebehandlung',
  package: 'Paket',
  subscription: 'Abo',
}

type StoredStatus = 'open' | 'paid' | 'cancelled'

const STATUS_LABELS: Record<string, string> = {
  open: 'Offen',
  paid: 'Bezahlt',
  expired: 'Abgelaufen',
  cancelled: 'Storniert',
}

const CHANNEL_LABELS: Record<CheckoutChannel, string> = {
  remote: 'Zahlungslink',
  counter: 'QR-Code am Tresen',
}

const PAYMENT_KIND_LABELS: Record<string, string> = {
  one_time: 'Einmalzahlung',
  installment: 'Ratenzahlung',
  limited_subscription: 'Abo',
}

type PlanState =
  'running' | 'completed' | 'paused' | 'cancelled' | 'not_completed' | 'refunded' | 'chargebacked'

const PLAN_STATE_LABELS: Record<PlanState, string> = {
  running: 'Läuft',
  completed: 'Abgeschlossen',
  paused: 'Pausiert',
  cancelled: 'Gekündigt',
  not_completed: 'Nicht abgeschlossen',
  refunded: 'Erstattet',
  chargebacked: 'Rückbuchung',
}

const CURRENCY = 'EUR'

const CHECKOUT_HOST = 'https://checkout.ablefy.example'

/** The selling Standort's Ablefy publisher and programme, carried by every link. */
const PUBLISHER = { pid: '20417', prid: '8836' }

/** How long an unpaid link counts as open (`aib.billing.checkout-expiry`). */
const CHECKOUT_EXPIRY_MS = 3 * 24 * 60 * 60 * 1000

/** How long the simulated customer takes to pay a new checkout. */
const PAYMENT_DELAY_MS = 15_000

/** How long Ablefy takes to confirm a refund with its webhook. */
const REFUND_DELAY_MS = 10_000

/** How long Ablefy takes to send the order event that reports a cancelled plan. */
const PLAN_EVENT_DELAY_MS = 10_000

interface DemoPayment {
  paidAt: string
  cents: number
  invoiceNumber: string
}

interface DemoPurchase {
  id: number
  customerId: string
  rowId: string
  channel: CheckoutChannel
  status: StoredStatus
  reference: number
  check: number
  payments: DemoPayment[]
  refundedCents: number
  consumed: number
  revoked: number
  creditShortfall: number
  /** As Ablefy's last order event left it; `null` until one arrives. */
  planState: PlanState | null
  planCancelsAt: number | null
  createdAt: string
  expiresAt: string
  paysAt: number | null
  refundsDue: { cents: number; confirmsAt: number }[]
  transferPending: boolean
  dashboardRefundCents: number
}

const purchases = new Map<string, DemoPurchase[]>()
const cashSales = new Map<string, { customerId: string; lotId: number }>()

interface DemoLedger {
  granted: number
  consumed: number
  reserved: number
}

const ledgers = new Map<string, DemoLedger>()

let nextPurchaseId = 4101
let nextLotId = 9001
let nextInvoiceNumber = 3400

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

const randomInt = () => 1 + Math.floor(Math.random() * 2_147_483_646)

function money(cents: number): Money {
  return { amount: (cents / 100).toFixed(2), currency: CURRENCY }
}

const notFound = (message: string) => new ApiError({ message, status: 404, code: 'NOT_FOUND' })

function conflict(
  code: 'NOTHING_TO_REFUND' | 'NOT_A_PLAN' | 'PLAN_COMPLETE' | 'PAYMENT_PENDING' | 'ABLEFY_REFUSED',
  message: string,
) {
  return new ApiError({ message, status: 409, code })
}

function invalidAmount(why: string): ApiError {
  const message = 'Die Eingabe ist unvollständig oder ungültig.'
  const details = { fields: { amount: why } }
  return new ApiError({
    message,
    status: 422,
    code: 'VALIDATION_FAILED',
    fieldErrors: details.fields,
    body: { code: 'VALIDATION_FAILED', message, details },
  })
}

/** Refunds, plan ends and cash sales are the Filialleiter's (`CREDITS_GRANT`). */
function requireCreditsGrant() {
  if (useAuthStore.getState().session?.role.key === 'branch_manager') return

  const message = 'Für diese Aktion fehlt die Berechtigung.'
  throw new ApiError({
    message,
    status: 403,
    code: 'CAPABILITY_REQUIRED',
    body: {
      code: 'CAPABILITY_REQUIRED',
      message,
      details: { requiredCapability: 'CREDITS_GRANT' },
    },
  })
}

/** A requested amount as the server reads it: a positive decimal with at most two places. */
function requestedCents(amount: string): number {
  if (!/^\d+(\.\d+)?$/.test(amount.trim())) throw invalidAmount('kein gültiger Betrag')
  const [, fraction = ''] = amount.trim().split('.')
  if (fraction.length > 2) throw invalidAmount('kein gültiger Betrag')

  const cents = toCents(amount.trim())
  if (cents <= 0) throw invalidAmount('muss größer als null sein')
  return cents
}

function findRow(rowId: string): DemoRow {
  const found = CATALOGUE.find((candidate) => candidate.id === rowId)
  if (!found) throw notFound('Dieses Paket wurde nicht gefunden.')
  return found
}

function totalCredits(row: DemoRow): number {
  return row.termMonths === null ? row.creditQty : row.creditQty * row.termMonths
}

function checkoutUrl(row: DemoRow, record: DemoPurchase): string {
  const query = new URLSearchParams({
    add_id_1: String(record.reference),
    add_id_2: String(record.check),
    pricing_plan_id: String(row.pricingPlanId),
    displayed_plans_id: String(row.pricingPlanId),
    pid: PUBLISHER.pid,
    prid: PUBLISHER.prid,
  })
  return `${CHECKOUT_HOST}/s/ail/${row.slug}/payment?${query}`
}

function statusOf(record: DemoPurchase): string {
  return record.status === 'open' && Date.now() >= new Date(record.expiresAt).getTime()
    ? 'expired'
    : record.status
}

function paymentKindOf(record: DemoPurchase, row: DemoRow): string | null {
  if (record.payments.length === 0) return null
  if (row.rateCount <= 1) return 'one_time'
  return row.kind === 'subscription' ? 'limited_subscription' : 'installment'
}

function creditsPerPayment(record: DemoPurchase, row: DemoRow): number[] {
  if (row.kind === 'subscription') return record.payments.map(() => row.creditQty)
  if (row.rateCount <= 1) return record.payments.slice(0, 1).map(() => row.creditQty)

  const base = Math.floor(row.creditQty / row.rateCount)
  const extra = row.creditQty % row.rateCount
  return record.payments.map((_, index) => (index < extra ? base + 1 : base))
}

function lotsOf(record: DemoPurchase, row: DemoRow): PurchaseLot[] {
  let unconsumed = record.consumed
  const lots = creditsPerPayment(record, row).map((credits, index) => {
    const consumed = Math.min(credits, unconsumed)
    unconsumed -= consumed
    return {
      id: String(record.id * 10 + index + 1),
      grantedAt: record.payments[index].paidAt,
      purchased: credits,
      consumed,
      revoked: 0,
      available: credits - consumed,
    }
  })

  let unrevoked = record.revoked
  for (const lot of [...lots].reverse()) {
    lot.revoked = Math.min(lot.available, unrevoked)
    lot.available -= lot.revoked
    unrevoked -= lot.revoked
  }
  return lots
}

const invoicePdfs = new Map<string, string>()

/**
 * A one-page PDF standing in for Ablefy's invoice, so the link opens a real
 * document. Built as WinAnsi bytes, the encoding of PDF's built-in Helvetica.
 */
function invoicePdfUrl(row: DemoRow, kindLabel: string, payment: DemoPayment): string {
  const cached = invoicePdfs.get(payment.invoiceNumber)
  if (cached) return cached

  const escape = (text: string) => text.replace(/[\\()]/g, '\\$&').replace(/€/g, '\x80')
  const lines = [
    `Paket: ${row.name}`,
    `Zahlungsart: ${kindLabel}`,
    `Betrag: ${formatPrice(money(payment.cents))}`,
    `Bezahlt am: ${new Date(payment.paidAt).toLocaleDateString('de-DE', { dateStyle: 'medium' })}`,
    'Demo-Beleg: Rechnungen stellt Ablefy aus (AF-05).',
  ]
  const content = [
    'BT',
    `/F1 20 Tf 56 770 Td (${escape(`Rechnung ${payment.invoiceNumber}`)}) Tj`,
    '/F1 11 Tf 16 TL T*',
    ...lines.map((line) => `T* (${escape(line)}) Tj`),
    'ET',
  ].join('\n')
  const objects = [
    '<< /Type /Catalog /Pages 2 0 R >>',
    '<< /Type /Pages /Kids [3 0 R] /Count 1 >>',
    '<< /Type /Page /Parent 2 0 R /MediaBox [0 0 595 842] /Resources << /Font << /F1 4 0 R >> >> /Contents 5 0 R >>',
    '<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica /Encoding /WinAnsiEncoding >>',
    `<< /Length ${content.length} >>\nstream\n${content}\nendstream`,
  ]

  let pdf = '%PDF-1.4\n'
  const offsets = objects.map((object, index) => {
    const offset = pdf.length
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`
    return offset
  })
  const xref = pdf.length
  pdf += `xref\n0 ${objects.length + 1}\n0000000000 65535 f \n`
  pdf += offsets.map((offset) => `${String(offset).padStart(10, '0')} 00000 n \n`).join('')
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\nstartxref\n${xref}\n%%EOF`

  const bytes = Uint8Array.from(pdf, (char) => char.charCodeAt(0))
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }))
  invoicePdfs.set(payment.invoiceNumber, url)
  return url
}

function toPurchase(record: DemoPurchase): Purchase {
  const row = findRow(record.rowId)
  const status = statusOf(record)
  const paymentKind = paymentKindOf(record, row)
  const paymentKindLabel = paymentKind === null ? null : PAYMENT_KIND_LABELS[paymentKind]
  const lots = lotsOf(record, row)
  const paidCents = record.payments.reduce((sum, payment) => sum + payment.cents, 0)
  const planState =
    record.planState ?? (record.payments.length >= row.rateCount ? 'completed' : 'running')

  return {
    id: String(record.id),
    packageName: row.name,
    packageKind: row.kind,
    packageKindLabel: KIND_LABELS[row.kind],
    paymentKind,
    paymentKindLabel,
    channel: record.channel,
    channelLabel: CHANNEL_LABELS[record.channel],
    status,
    statusLabel: STATUS_LABELS[status],
    credits: totalCredits(row),
    creditsGranted: lots.reduce((sum, lot) => sum + lot.purchased, 0),
    creditsRevoked: record.revoked,
    creditShortfall: record.creditShortfall,
    paid: record.payments.length === 0 ? null : money(paidCents),
    refunded: record.refundedCents === 0 ? null : money(record.refundedCents),
    plan:
      row.rateCount <= 1 || record.payments.length === 0
        ? null
        : {
            state: planState,
            stateLabel: PLAN_STATE_LABELS[planState],
            ratesPaid: record.payments.length,
            rateCount: row.rateCount,
          },
    lots,
    invoices: record.payments.map((payment) => ({
      number: payment.invoiceNumber,
      url: invoicePdfUrl(row, paymentKindLabel ?? '', payment),
    })),
    createdAt: record.createdAt,
    paidAt: record.payments[0]?.paidAt ?? null,
    expiresAt: record.expiresAt,
    checkoutUrl: status === 'open' || status === 'expired' ? checkoutUrl(row, record) : null,
  }
}

function payments(row: DemoRow, dates: string[], invoiceNumbers: number[]): DemoPayment[] {
  return dates.map((paidAt, index) => ({
    paidAt,
    cents: row.rateCents,
    invoiceNumber: `RE-2026-${String(invoiceNumbers[index]).padStart(5, '0')}`,
  }))
}

type SeedRecord = Omit<
  DemoPurchase,
  | 'id'
  | 'customerId'
  | 'reference'
  | 'check'
  | 'planState'
  | 'planCancelsAt'
  | 'paysAt'
  | 'refundsDue'
  | 'transferPending'
  | 'dashboardRefundCents'
> &
  Partial<Pick<DemoPurchase, 'planState' | 'transferPending' | 'dashboardRefundCents'>>

/**
 * One history per customer, oldest first: rates Ablefy reports as not
 * completed, a refunded trial, a withdrawn link, completed rates paid at the
 * counter, a package refunded meanwhile in Ablefy's dashboard
 * (NOTHING_TO_REFUND), a running Abo, a package partly refunded meanwhile in
 * the dashboard (the 422 on `amount`), a package refunded after three
 * treatments (the shortfall), running rates, paused rates, a lapsed link, an
 * open one and a trial whose payment Ablefy still settles (PAYMENT_PENDING).
 */
function seed(customerId: string): DemoPurchase[] {
  const [abo, basic10, basic10Rates, basic20, basic20Rates, trial] = CATALOGUE
  const openedAt = Date.now() - 20 * 60 * 60 * 1000
  const trialPaidAt = Date.now() - 2 * 60 * 60 * 1000

  const records: SeedRecord[] = [
    {
      rowId: basic20Rates.id,
      channel: 'remote',
      status: 'paid',
      payments: payments(
        basic20Rates,
        ['2026-05-28T10:06:00+02:00', '2026-06-28T09:00:00+02:00'],
        [2311, 2502],
      ),
      refundedCents: 0,
      consumed: 8,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-05-28T10:00:00+02:00',
      expiresAt: '2026-05-31T10:00:00+02:00',
      planState: 'not_completed',
    },
    {
      rowId: trial.id,
      channel: 'remote',
      status: 'paid',
      payments: payments(trial, ['2026-06-10T18:09:00+02:00'], [2390]),
      refundedCents: 4900,
      consumed: 0,
      revoked: 1,
      creditShortfall: 0,
      createdAt: '2026-06-10T18:05:00+02:00',
      expiresAt: '2026-06-13T18:05:00+02:00',
    },
    {
      rowId: basic20.id,
      channel: 'remote',
      status: 'cancelled',
      payments: [],
      refundedCents: 0,
      consumed: 0,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-06-18T11:30:00+02:00',
      expiresAt: '2026-06-21T11:30:00+02:00',
    },
    {
      rowId: basic10Rates.id,
      channel: 'counter',
      status: 'paid',
      payments: payments(
        basic10Rates,
        ['2026-07-02T10:19:00+02:00', '2026-08-02T09:00:00+02:00', '2026-09-02T09:00:00+02:00'],
        [2688, 2901, 3122],
      ),
      refundedCents: 0,
      consumed: 6,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-07-02T10:15:00+02:00',
      expiresAt: '2026-07-05T10:15:00+02:00',
    },
    {
      rowId: basic10.id,
      channel: 'remote',
      status: 'paid',
      payments: payments(basic10, ['2026-07-08T12:04:00+02:00'], [2731]),
      refundedCents: 0,
      consumed: 2,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-07-08T12:00:00+02:00',
      expiresAt: '2026-07-11T12:00:00+02:00',
      dashboardRefundCents: 140000,
    },
    {
      rowId: abo.id,
      channel: 'remote',
      status: 'paid',
      payments: payments(
        abo,
        ['2026-07-15T14:31:00+02:00', '2026-08-15T09:00:00+02:00', '2026-09-15T09:00:00+02:00'],
        [2815, 3030, 3251],
      ),
      refundedCents: 0,
      consumed: 5,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-07-15T14:20:00+02:00',
      expiresAt: '2026-07-18T14:20:00+02:00',
    },
    {
      rowId: basic20.id,
      channel: 'counter',
      status: 'paid',
      payments: payments(basic20, ['2026-07-25T16:41:00+02:00'], [2874]),
      refundedCents: 0,
      consumed: 5,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-07-25T16:38:00+02:00',
      expiresAt: '2026-07-28T16:38:00+02:00',
      dashboardRefundCents: 50000,
    },
    {
      rowId: basic10.id,
      channel: 'remote',
      status: 'paid',
      payments: payments(basic10, ['2026-08-05T17:52:00+02:00'], [2960]),
      refundedCents: 140000,
      consumed: 3,
      revoked: 7,
      creditShortfall: 3,
      createdAt: '2026-08-05T17:45:00+02:00',
      expiresAt: '2026-08-08T17:45:00+02:00',
    },
    {
      rowId: basic20Rates.id,
      channel: 'remote',
      status: 'paid',
      payments: payments(
        basic20Rates,
        ['2026-08-21T08:12:00+02:00', '2026-09-21T09:00:00+02:00'],
        [3019, 3260],
      ),
      refundedCents: 0,
      consumed: 4,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-08-20T11:00:00+02:00',
      expiresAt: '2026-08-23T11:00:00+02:00',
    },
    {
      rowId: basic10Rates.id,
      channel: 'counter',
      status: 'paid',
      payments: payments(basic10Rates, ['2026-09-08T15:22:00+02:00'], [3172]),
      refundedCents: 0,
      consumed: 1,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-09-08T15:20:00+02:00',
      expiresAt: '2026-09-11T15:20:00+02:00',
      planState: 'paused',
    },
    {
      rowId: basic20Rates.id,
      channel: 'remote',
      status: 'open',
      payments: [],
      refundedCents: 0,
      consumed: 0,
      revoked: 0,
      creditShortfall: 0,
      createdAt: '2026-10-01T16:30:00+02:00',
      expiresAt: '2026-10-04T16:30:00+02:00',
    },
    {
      rowId: basic10.id,
      channel: 'remote',
      status: 'open',
      payments: [],
      refundedCents: 0,
      consumed: 0,
      revoked: 0,
      creditShortfall: 0,
      createdAt: new Date(openedAt).toISOString(),
      expiresAt: new Date(openedAt + CHECKOUT_EXPIRY_MS).toISOString(),
    },
    {
      rowId: trial.id,
      channel: 'remote',
      status: 'paid',
      payments: payments(trial, [new Date(trialPaidAt).toISOString()], [3398]),
      refundedCents: 0,
      consumed: 0,
      revoked: 0,
      creditShortfall: 0,
      createdAt: new Date(trialPaidAt - 5 * 60 * 1000).toISOString(),
      expiresAt: new Date(trialPaidAt - 5 * 60 * 1000 + CHECKOUT_EXPIRY_MS).toISOString(),
      transferPending: true,
    },
  ]

  return records
    .map((record) => ({
      transferPending: false,
      dashboardRefundCents: 0,
      planState: null,
      ...record,
      id: nextPurchaseId++,
      customerId,
      reference: randomInt(),
      check: randomInt(),
      planCancelsAt: null,
      paysAt: null,
      refundsDue: [],
    }))
    .reverse()
}

function paidCentsOf(record: DemoPurchase): number {
  return record.payments.reduce((sum, payment) => sum + payment.cents, 0)
}

/**
 * A confirmed refund, as the server books it: the refunded share of the price
 * takes back the same share of the credits, and whatever of that share is
 * already used becomes the shortfall.
 */
function applyRefund(record: DemoPurchase, requestedCents: number) {
  const paidCents = paidCentsOf(record)
  const cents = Math.min(requestedCents, paidCents - record.refundedCents)
  if (cents <= 0) return

  const granted = lotsOf(record, findRow(record.rowId)).reduce((sum, lot) => sum + lot.purchased, 0)
  const toRevoke = Math.floor((cents * granted) / paidCents)
  const revocable = granted - record.consumed - record.revoked
  const revoked = Math.min(toRevoke, revocable)

  record.refundedCents += cents
  record.revoked += revoked
  record.creditShortfall += toRevoke - revoked
}

/** Stands in for Ablefy's `refund.successful` webhook, a fixed time after the request. */
function settleDueRefunds(list: DemoPurchase[]) {
  const now = Date.now()

  for (const record of list) {
    const due = record.refundsDue.filter((refund) => refund.confirmsAt <= now)
    if (due.length === 0) continue

    record.refundsDue = record.refundsDue.filter((refund) => refund.confirmsAt > now)
    for (const refund of due) applyRefund(record, refund.cents)
  }
}

/**
 * Stands in for the order event Ablefy is expected to send after a staff
 * cancel (`payment_canceled`). The rates paid stay as they are.
 */
function settleDuePlanEvents(list: DemoPurchase[]) {
  const now = Date.now()

  for (const record of list) {
    if (record.planCancelsAt === null || record.planCancelsAt > now) continue

    record.planState = 'cancelled'
    record.planCancelsAt = null
  }
}

/**
 * Stands in for Ablefy's webhook: the customer pays a new checkout a fixed time
 * after it was minted, so the payment dialog's polling sees it turn paid and
 * Ablefy's invoice appears.
 */
function settleDuePayments(list: DemoPurchase[]) {
  const now = Date.now()

  for (const record of list) {
    if (record.status !== 'open' || record.paysAt === null || record.paysAt > now) continue

    const row = findRow(record.rowId)
    record.status = 'paid'
    record.payments = payments(row, [new Date(record.paysAt).toISOString()], [nextInvoiceNumber++])
  }
}

function purchasesOf(customerId: string): DemoPurchase[] {
  let list = purchases.get(customerId)
  if (!list) {
    list = seed(customerId)
    purchases.set(customerId, list)
  }
  settleDuePayments(list)
  settleDueRefunds(list)
  settleDuePlanEvents(list)
  return list
}

function ledgerOf(customerId: string): DemoLedger {
  let ledger = ledgers.get(customerId)
  if (!ledger) {
    ledger = { granted: 0, consumed: 0, reserved: 0 }
    ledgers.set(customerId, ledger)
  }
  return ledger
}

function grantedOf(record: DemoPurchase): number {
  return creditsPerPayment(record, findRow(record.rowId)).reduce((sum, credits) => sum + credits, 0)
}

function findPurchase(purchaseId: string): DemoPurchase {
  for (const list of purchases.values()) {
    const found = list.find((candidate) => String(candidate.id) === purchaseId)
    if (found) return found
  }
  throw notFound('Dieser Kauf wurde nicht gefunden.')
}

export const demoBilling = {
  fetchPackages: async (): Promise<BillingPackage[]> => {
    await wait(240)
    return CATALOGUE.map((row) => ({
      id: row.id,
      name: row.name,
      kind: row.kind,
      kindLabel: KIND_LABELS[row.kind],
      credits: totalCredits(row),
      termMonths: row.termMonths,
      price: money(row.rateCents * row.rateCount),
      rateCount: row.rateCount > 1 ? row.rateCount : null,
      rateAmount: row.rateCount > 1 ? money(row.rateCents) : null,
    }))
  },

  /** The purchase opens as `open` and shows up in the history until it is paid. */
  createCheckoutIntent: async (input: CheckoutIntentInput): Promise<CheckoutIntent> => {
    await wait(400)
    const row = findRow(input.packageId)
    const now = Date.now()

    const record: DemoPurchase = {
      id: nextPurchaseId++,
      customerId: input.customerId,
      rowId: row.id,
      channel: input.channel,
      status: 'open',
      reference: randomInt(),
      check: randomInt(),
      payments: [],
      refundedCents: 0,
      consumed: 0,
      revoked: 0,
      creditShortfall: 0,
      planState: null,
      planCancelsAt: null,
      createdAt: new Date(now).toISOString(),
      expiresAt: new Date(now + CHECKOUT_EXPIRY_MS).toISOString(),
      paysAt: now + PAYMENT_DELAY_MS,
      refundsDue: [],
      transferPending: false,
      dashboardRefundCents: 0,
    }
    purchasesOf(input.customerId).unshift(record)

    return {
      id: String(record.id),
      customerId: record.customerId,
      channel: record.channel,
      channelLabel: CHANNEL_LABELS[record.channel],
      packageName: row.name,
      status: record.status,
      statusLabel: STATUS_LABELS[record.status],
      checkoutUrl: checkoutUrl(row, record),
      createdAt: record.createdAt,
      expiresAt: record.expiresAt,
    }
  },

  fetchPurchases: async (customerId: string): Promise<Purchase[]> => {
    await wait(240)
    return purchasesOf(customerId).map(toPurchase)
  },

  /**
   * `202`, checked in the server's order. Like the server, it measures what is
   * refundable against confirmed refunds only; the refund itself lands after
   * `REFUND_DELAY_MS`, when the next read settles it.
   */
  refund: async (purchaseId: string, input: RefundInput): Promise<RefundResult> => {
    await wait(400)
    const requested = input.amount === null ? null : requestedCents(input.amount)
    requireCreditsGrant()
    const record = findPurchase(purchaseId)

    if (record.dashboardRefundCents > 0) {
      applyRefund(record, record.dashboardRefundCents)
      record.dashboardRefundCents = 0
    }

    const remaining = paidCentsOf(record) - record.refundedCents
    if (remaining <= 0) {
      throw conflict('NOTHING_TO_REFUND', 'Für diesen Kauf ist nichts mehr zu erstatten.')
    }

    const cents = requested ?? remaining
    if (cents > remaining) throw invalidAmount('übersteigt den erstattbaren Betrag')

    if (record.transferPending) {
      throw conflict(
        'PAYMENT_PENDING',
        'Die Zahlung wird noch verarbeitet. Bitte später erneut versuchen.',
      )
    }

    record.refundsDue.push({ cents, confirmsAt: Date.now() + REFUND_DELAY_MS })
    return {
      purchaseId: String(record.id),
      status: 'requested',
      statusLabel: 'Angefordert',
      amount: money(cents),
    }
  },

  /**
   * Future rates stop; paid ones and their credits stay. Like the server it
   * writes no state: the plan reads `cancelled` once the simulated order event
   * lands, `PLAN_EVENT_DELAY_MS` later.
   */
  endPlan: async (purchaseId: string): Promise<EndPlanResult> => {
    await wait(400)
    requireCreditsGrant()
    const record = findPurchase(purchaseId)
    const row = findRow(record.rowId)

    if (row.rateCount <= 1 || record.payments.length === 0) {
      throw conflict('NOT_A_PLAN', 'Dieser Kauf hat keinen laufenden Zahlungsplan.')
    }
    if (record.payments.length >= row.rateCount) {
      throw conflict('PLAN_COMPLETE', 'Der Zahlungsplan ist bereits vollständig bezahlt.')
    }
    if (record.planCancelsAt !== null || record.planState === 'cancelled') {
      throw conflict('ABLEFY_REFUSED', 'Ablefy hat den Vorgang abgelehnt.')
    }

    record.planCancelsAt = Date.now() + PLAN_EVENT_DELAY_MS
    return {
      purchaseId: String(record.id),
      ratesCancelled: row.rateCount - record.payments.length,
    }
  },

  /** "Bar erhalten": a lot for the customer, once per `requestId`, and no purchase. */
  recordCashSale: async (input: CashSaleInput): Promise<CashSaleResult> => {
    await wait(400)
    requireCreditsGrant()

    const row = findRow(input.packageId)
    if (row.kind === 'subscription') {
      throw new ApiError({
        message: 'Ein Abo wird nicht bar verkauft.',
        status: 409,
        code: 'NOT_FOR_CASH',
      })
    }

    // As on the server, only another customer makes a reused id a conflict; a repeat for the
    // same customer names the row asked for now, even if the lot was granted for another.
    const earlier = cashSales.get(input.requestId)
    if (earlier && earlier.customerId !== input.customerId) {
      throw new ApiError({
        message: 'Diese Anfrage gehört bereits zu einem anderen Verkauf.',
        status: 409,
        code: 'REQUEST_REUSED',
      })
    }

    const sale = earlier ?? { customerId: input.customerId, lotId: nextLotId++ }
    cashSales.set(input.requestId, sale)
    if (!earlier) ledgerOf(input.customerId).granted += row.creditQty

    return {
      lotId: String(sale.lotId),
      packageName: row.name,
      credits: row.creditQty,
      repeated: earlier !== undefined,
    }
  },

  /** The balance as the server computes it from every lot; reserved credit is not available. */
  creditsOf: (customerId: string): Credits => {
    const ledger = ledgerOf(customerId)
    const list = purchasesOf(customerId)
    const purchased = list.reduce((sum, record) => sum + grantedOf(record), ledger.granted)
    const consumed = list.reduce((sum, record) => sum + record.consumed, ledger.consumed)
    const revoked = list.reduce((sum, record) => sum + record.revoked, 0)

    return {
      purchased,
      consumed,
      reserved: ledger.reserved,
      available: Math.max(0, purchased - consumed - revoked - ledger.reserved),
      openContingent: 0,
    }
  },

  grantCredits: (customerId: string, quantity: number) => {
    ledgerOf(customerId).granted += quantity
  },

  reserveCredit: (customerId: string) => {
    ledgerOf(customerId).reserved += 1
  },

  consumeCredit: (customerId: string) => {
    const ledger = ledgerOf(customerId)
    ledger.reserved = Math.max(0, ledger.reserved - 1)

    const owner = [...purchasesOf(customerId)]
      .reverse()
      .find((record) => grantedOf(record) - record.consumed - record.revoked > 0)
    if (owner) owner.consumed += 1
    else ledger.consumed += 1
  },
}
